import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import {
  appelerApi,
  corpsJson,
  ouvrirRessourceApi,
  telechargerRessourceApi
} from "../api/client";
import type { Organisation, TableauBord } from "../api/types";
import { utiliserSession } from "../auth/SessionContext";
import { formatDate } from "../utils/format";

function CarteIndicateur({ valeur, libelle }: { valeur: number; libelle: string }) {
  return (
    <article className="indicateur">
      <strong>{valeur}</strong>
      <span>{libelle}</span>
    </article>
  );
}

export function EspacePage() {
  const { session } = utiliserSession();
  const [organisationId, definirOrganisationId] = useState<number | null>(
    session?.organisations[0]?.id ?? null
  );
  const [tableauBord, definirTableauBord] = useState<TableauBord | null>(null);
  const [erreur, definirErreur] = useState("");
  const [actualisation, definirActualisation] = useState(0);

  const organisation = useMemo(
    () => session?.organisations.find((element) => element.id === organisationId) ?? null,
    [session, organisationId]
  );

  useEffect(() => {
    if (!organisation) return;
    const ressource =
      organisation.type === "PRODUCTEUR"
        ? `/producteurs/${organisation.id}/tableau-de-bord`
        : `/structures/${organisation.id}/tableau-de-bord`;
    appelerApi<TableauBord>(ressource, {}, true)
      .then(definirTableauBord)
      .catch((cause: Error) => definirErreur(cause.message));
  }, [organisation, actualisation]);

  if (!session) return <Navigate to="/connexion" replace />;
  if (!organisation) {
    return (
      <section className="page">
        <div className="etat-vide">
          <strong>Aucune organisation active.</strong>
          <p>Contactez l’administration Tickboss pour rejoindre votre organisation.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="page espace-page">
      <header className="espace-entete">
        <div>
          <span className="sur-titre sur-titre--sombre">Bonjour {session.utilisateur.nom}</span>
          <h1>{organisation.nom}</h1>
          <p>
            {organisation.type === "PRODUCTEUR"
              ? "Pilotez vos fiches, disponibilités et demandes de programmation."
              : "Préparez vos sélections et construisez vos prochaines saisons."}
          </p>
        </div>
        {session.organisations.length > 1 && (
          <label className="champ">
            <span>Organisation active</span>
            <select
              value={organisation.id}
              onChange={(e) => definirOrganisationId(Number(e.target.value))}
            >
              {session.organisations.map((element) => (
                <option key={element.id} value={element.id}>
                  {element.nom}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>
      {erreur && <p className="alerte alerte--erreur">{erreur}</p>}
      <div className="grille-indicateurs">
        {Object.entries(tableauBord?.indicateurs ?? {}).map(([libelle, valeur]) => (
          <CarteIndicateur
            key={libelle}
            valeur={valeur}
            libelle={libelle.replaceAll("_", " ")}
          />
        ))}
      </div>
      {organisation.type === "PRODUCTEUR" ? (
        <EspaceProducteur
          organisation={organisation}
          tableauBord={tableauBord}
          apresCreation={() => definirActualisation((valeur) => valeur + 1)}
        />
      ) : (
        <EspaceStructure
          organisation={organisation}
          tableauBord={tableauBord}
          apresCreation={() => definirActualisation((valeur) => valeur + 1)}
        />
      )}
    </section>
  );
}

function EspaceProducteur({
  organisation,
  tableauBord,
  apresCreation
}: {
  organisation: Organisation;
  tableauBord: TableauBord | null;
  apresCreation: () => void;
}) {
  const [formulaireOuvert, definirFormulaireOuvert] = useState(false);
  const [titre, definirTitre] = useState("");
  const [compagnie, definirCompagnie] = useState(organisation.nom);
  const [resume, definirResume] = useState("");
  const [discipline, definirDiscipline] = useState("Théâtre");
  const [erreur, definirErreur] = useState("");

  async function creer(evenement: FormEvent) {
    evenement.preventDefault();
    definirErreur("");
    try {
      await appelerApi(
        "/productions",
        {
          method: "POST",
          body: corpsJson({
            organisation_id: organisation.id,
            titre,
            compagnie,
            resume_court: resume,
            classification: { disciplines: [discipline] },
            informations_pratiques: {},
            informations_commerciales: {},
            informations_commerciales_privees: true,
            publiee: false
          })
        },
        true
      );
      definirTitre("");
      definirResume("");
      definirFormulaireOuvert(false);
      apresCreation();
    } catch (cause) {
      definirErreur(cause instanceof Error ? cause.message : "Création impossible");
    }
  }

  return (
    <div className="espace-contenu">
      <section className="panneau-large">
        <header className="titre-section">
          <div>
            <span className="sur-titre sur-titre--sombre">Vos productions</span>
            <h2>Fiches de diffusion</h2>
          </div>
          <button
            className="bouton bouton--primaire"
            type="button"
            onClick={() => definirFormulaireOuvert((valeur) => !valeur)}
          >
            {formulaireOuvert ? "Fermer" : "Nouvelle production"}
          </button>
        </header>
        {formulaireOuvert && (
          <form className="formulaire-inline" onSubmit={creer}>
            <label className="champ">
              <span>Titre</span>
              <input required minLength={2} value={titre} onChange={(e) => definirTitre(e.target.value)} />
            </label>
            <label className="champ">
              <span>Compagnie</span>
              <input required value={compagnie} onChange={(e) => definirCompagnie(e.target.value)} />
            </label>
            <label className="champ">
              <span>Discipline</span>
              <select value={discipline} onChange={(e) => definirDiscipline(e.target.value)}>
                <option>Théâtre</option>
                <option>Danse</option>
                <option>Musique</option>
                <option>Cirque</option>
                <option>Humour</option>
              </select>
            </label>
            <label className="champ champ--large">
              <span>Résumé court</span>
              <textarea
                required
                minLength={20}
                maxLength={600}
                rows={3}
                value={resume}
                onChange={(e) => definirResume(e.target.value)}
              />
            </label>
            {erreur && <p className="alerte alerte--erreur">{erreur}</p>}
            <button className="bouton bouton--secondaire">Créer le brouillon</button>
          </form>
        )}
        <div className="liste-gestion">
          {tableauBord?.productions?.map((production) => (
            <article key={production.id}>
              <div>
                <Link to={`/productions/${production.id}`}>{production.titre}</Link>
                <span>{production.statut.replaceAll("_", " ")}</span>
              </div>
              <div className="liste-gestion__meta">
                <span>Version {production.version}</span>
                <strong className={production.publiee ? "statut-publie" : "statut-brouillon"}>
                  {production.publiee ? "Publiée" : "Brouillon"}
                </strong>
              </div>
            </article>
          ))}
          {!tableauBord?.productions?.length && (
            <div className="etat-vide etat-vide--compact">
              <strong>Créez votre première fiche.</strong>
              <p>Vous pourrez l’enrichir et la publier quand elle sera prête.</p>
            </div>
          )}
        </div>
      </section>
      <aside className="panneau-conseil">
        <span className="sur-titre">Qualité de la fiche</span>
        <h2>Une information saisie une seule fois.</h2>
        <ol>
          <li>Complétez les contraintes techniques</li>
          <li>Ajoutez des visuels avec leurs crédits</li>
          <li>Renseignez vos prochaines disponibilités</li>
          <li>Publiez puis partagez le lien stable</li>
        </ol>
      </aside>
    </div>
  );
}

function EspaceStructure({
  organisation,
  tableauBord,
  apresCreation
}: {
  organisation: Organisation;
  tableauBord: TableauBord | null;
  apresCreation: () => void;
}) {
  const [nom, definirNom] = useState("");
  const [dateDebut, definirDateDebut] = useState("");
  const [dateFin, definirDateFin] = useState("");
  const [erreur, definirErreur] = useState("");
  const [recommandations, definirRecommandations] = useState<
    Array<{ production_id: number; titre: string; score: number; raisons: string[] }>
  >([]);

  function lancerExport(action: Promise<void>) {
    definirErreur("");
    void action.catch((cause: unknown) =>
      definirErreur(cause instanceof Error ? cause.message : "Export impossible")
    );
  }

  async function creerSaison(evenement: FormEvent) {
    evenement.preventDefault();
    definirErreur("");
    try {
      await appelerApi(
        "/saisons",
        {
          method: "POST",
          body: corpsJson({
            organisation_id: organisation.id,
            nom,
            date_debut: dateDebut,
            date_fin: dateFin,
            personnalisation: {}
          })
        },
        true
      );
      definirNom("");
      definirDateDebut("");
      definirDateFin("");
      apresCreation();
    } catch (cause) {
      definirErreur(cause instanceof Error ? cause.message : "Création impossible");
    }
  }

  async function chargerRecommandations() {
    definirErreur("");
    try {
      const resultat = await appelerApi<{
        resultats: Array<{
          production_id: number;
          titre: string;
          score: number;
          raisons: string[];
        }>;
      }>(
        "/recommandations",
        {
          method: "POST",
          body: corpsJson({ organisation_id: organisation.id, taille: 5 })
        },
        true
      );
      definirRecommandations(resultat.resultats);
    } catch (cause) {
      definirErreur(cause instanceof Error ? cause.message : "Suggestions indisponibles");
    }
  }

  return (
    <div className="espace-contenu">
      <section className="panneau-large">
        <span className="sur-titre sur-titre--sombre">Programmation</span>
        <h2>Vos saisons culturelles</h2>
        <div className="liste-gestion">
          {tableauBord?.saisons?.map((saison) => (
            <article key={saison.id}>
              <div>
                <strong>{saison.nom}</strong>
                <span>
                  {formatDate(saison.date_debut)} — {formatDate(saison.date_fin)}
                </span>
              </div>
              <div className="liste-gestion__meta">
                <strong className="statut-brouillon">{saison.statut}</strong>
                <button
                  className="bouton bouton--discret"
                  type="button"
                  onClick={() =>
                    lancerExport(
                      ouvrirRessourceApi(`/saisons/${saison.id}/page.html`)
                    )
                  }
                >
                  Prévisualiser
                </button>
                <button
                  className="bouton bouton--discret"
                  type="button"
                  onClick={() =>
                    lancerExport(
                      telechargerRessourceApi(
                        `/saisons/${saison.id}/brochure.pdf`,
                        `saison-${saison.id}.pdf`
                      )
                    )
                  }
                >
                  Télécharger le PDF
                </button>
              </div>
            </article>
          ))}
        </div>
        <form className="formulaire-inline formulaire-saison" onSubmit={creerSaison}>
          <h3>Préparer une nouvelle saison</h3>
          <label className="champ">
            <span>Nom</span>
            <input required minLength={2} value={nom} onChange={(e) => definirNom(e.target.value)} />
          </label>
          <label className="champ">
            <span>Début</span>
            <input type="date" required value={dateDebut} onChange={(e) => definirDateDebut(e.target.value)} />
          </label>
          <label className="champ">
            <span>Fin</span>
            <input type="date" required value={dateFin} onChange={(e) => definirDateFin(e.target.value)} />
          </label>
          {erreur && <p className="alerte alerte--erreur">{erreur}</p>}
          <button className="bouton bouton--secondaire">Créer la saison</button>
        </form>
      </section>
      <aside className="panneau-conseil panneau-conseil--clair">
        <span className="sur-titre sur-titre--sombre">Votre sélection</span>
        <h2>{tableauBord?.favoris?.length ?? 0} spectacle(s) en favoris</h2>
        <p>Explorez le catalogue, enregistrez les fiches et importez-les sans ressaisie.</p>
        <Link className="bouton bouton--primaire" to="/catalogue">
          Parcourir le catalogue
        </Link>
        <button
          className="bouton bouton--secondaire"
          type="button"
          onClick={() => void chargerRecommandations()}
        >
          Suggérer des productions
        </button>
        {!!recommandations.length && (
          <div className="suggestions-espace">
            {recommandations.map((recommandation) => (
              <Link
                key={recommandation.production_id}
                to={`/productions/${recommandation.production_id}`}
              >
                <span>{recommandation.titre}</span>
                <strong>{recommandation.score} %</strong>
                <small>{recommandation.raisons[0]}</small>
              </Link>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}
